import { useEffect, useState } from "react";
import dayjs from "dayjs";
import DeliveryCenterCostsService from "../../api/DeliveryCenter/deliveryCenterCosts";

/**
 * Custom hook to manage costs data, including fetching and calculations
 * @returns {Object} Data and functions for costs management
 */
const useCostsData = () => {
  // Data states
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categorySummary, setCategorySummary] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [filteredCosts, setFilteredCosts] = useState([]);

  // Statistics states
  const [totalCosts, setTotalCosts] = useState(0);
  const [averageCost, setAverageCost] = useState(0);
  const [highestCost, setHighestCost] = useState(0);
  const [costCount, setCostCount] = useState(0);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches all data from the API
   * @param {Object} dateFilters - Optional date filters for summary data
   */
  const fetchAllData = async (dateFilters = {}) => {
    const startDate =
      dateFilters.startDate || dayjs().startOf("month").format("YYYY-MM-DD");
    const endDate = dateFilters.endDate || dayjs().format("YYYY-MM-DD");
    const year = dateFilters.year || dayjs().year();

    setLoading(true);
    setError(null);

    try {
      // Fetch data in parallel for performance
      const [
        costsResponse,
        categoriesResponse,
        suppliersResponse,
        categorySummaryResponse,
        weeklySummaryResponse,
      ] = await Promise.all([
        DeliveryCenterCostsService.getCosts(),
        DeliveryCenterCostsService.getCostCategories(),
        DeliveryCenterCostsService.getSuppliers(),
        DeliveryCenterCostsService.getCostSummaryByCategory({
          startDate,
          endDate,
        }),
        DeliveryCenterCostsService.getCostSummaryByWeek({ year }),
      ]);

      // Process responses
      const costsData = costsResponse.data.results || costsResponse.data;
      const categoriesData =
        categoriesResponse.data.results || categoriesResponse.data;
      const suppliersData =
        suppliersResponse.data.results || suppliersResponse.data;
      const categorySum = categorySummaryResponse.data;
      const weeklySum = weeklySummaryResponse.data;

      // Update state
      setCosts(costsData);
      setFilteredCosts(costsData);
      setCategories(categoriesData);
      setSuppliers(suppliersData);
      setCategorySummary(categorySum);
      setWeeklySummary(weeklySum);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deletes a cost record
   * @param {string|number} costId - ID of the cost to delete
   * @returns {Promise<boolean>} Success status
   */
  const deleteCost = async (costId) => {
    try {
      await DeliveryCenterCostsService.deleteCost(costId);
      return true;
    } catch (err) {
      console.error("Error deleting cost:", err);
      setError("Failed to delete cost. Please try again.");
      return false;
    }
  };

  /**
   * Gets category name from ID
   * @param {string|number} categoryId - Category ID
   * @returns {string} Category name
   */
  const getCategoryName = (categoryId) => {
    if (!categoryId) return "N/A";
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "Unknown";
  };

  /**
   * Gets supplier name from ID
   * @param {string|number} supplierId - Supplier ID
   * @returns {string} Supplier name
   */
  const getSupplierName = (supplierId) => {
    if (!supplierId) return "N/A";
    const supplier = suppliers.find((s) => s.id === supplierId);
    return supplier ? supplier.name : "Unknown";
  };

  /**
   * Calculates statistics from filtered costs
   */
  const calculateStatistics = () => {
    if (filteredCosts.length === 0) {
      setTotalCosts(0);
      setAverageCost(0);
      setHighestCost(0);
      setCostCount(0);
      return;
    }

    const total = filteredCosts.reduce((sum, cost) => sum + cost.value, 0);
    const highest = Math.max(...filteredCosts.map((cost) => cost.value));
    const average = total / filteredCosts.length;

    setTotalCosts(total);
    setAverageCost(average);
    setHighestCost(highest);
    setCostCount(filteredCosts.length);
  };

  // Calculate statistics when filtered costs change
  useEffect(() => {
    calculateStatistics();
  }, [filteredCosts]);

  // Format costs data for export
  const getExportData = () => {
    const headers = [
      "Date",
      "Category",
      "Supplier",
      "Invoice",
      "Value",
      "Week",
      "Notes",
    ];
    const data = filteredCosts.map((cost) => [
      cost.date,
      getCategoryName(cost.category),
      getSupplierName(cost.supplier),
      cost.invoice_number || "",
      cost.value,
      cost.week_number,
      cost.notes || "",
    ]);

    return { headers, data };
  };

  return {
    // Data
    costs,
    categories,
    suppliers,
    categorySummary,
    weeklySummary,
    filteredCosts,
    setFilteredCosts,

    // Statistics
    totalCosts,
    averageCost,
    highestCost,
    costCount,

    // UI states
    loading,
    error,

    // Functions
    fetchAllData,
    deleteCost,
    getCategoryName,
    getSupplierName,
    getExportData,
  };
};

export default useCostsData;
